/* eslint-disable max-lines -- a data table with one entry per management section */
import type { ComponentType } from "react"

import type { CapabilityMode } from "@/api/admin/delegation"

import {
  BarChart3,
  Bell,
  Bot,
  Brain,
  Database,
  FileText,
  GraduationCap,
  ImagePlus,
  Layers,
  type LucideIcon,
  Mail,
  MonitorPlay,
  Palette,
  PlugZap,
  Presentation,
  RefreshCw,
  Route,
  Server,
  Shield,
  Sparkles,
  Workflow,
  Wrench,
  Zap,
} from "@/components/icons"
import { PluginsAdmin } from "@/features/plugins/plugins-admin"

import { AdminGroupsPanel } from "./components/admin-groups-panel"
import { AlertsConfigPanel } from "./components/alerts/alerts-config-panel"
import { AuditTrailSection } from "./components/audit-trail-section"
import { BrandingConfigPanel } from "./components/branding-config-panel"
import { DataConnectionsManagementCard } from "./components/data-connections-management-card"
import { DocintConfigPanel } from "./components/docint/docint-config-panel"
import { FeaturesManagementCard } from "./components/features-management-card"
import { FilthBackendsManagementCard } from "./components/filth-backends-management-card"
import { FlowCreationPolicyPanel } from "./components/flow-creation-policy-panel"
import { FlowEmailBrandingPanel } from "./components/flow-email-branding-panel"
import { HostedAgentsManagementCard } from "./components/hosted-agents-management-card"
import { ImageModelConfigPanel } from "./components/image-model-config-panel"
import { IndexingManagementSection } from "./components/indexing-management-section"
import { LibrariesManagementCard } from "./components/libraries-management-card"
import { LLMPoolConfigPanel } from "./components/llm-pool-config-panel"
import { McpsManagementCard } from "./components/mcps-management-card"
import { MiddlewareManagementSection } from "./components/middleware-management-section"
import { ModelsManagementCard } from "./components/models-management-card"
import { PersonalSourcesCard } from "./components/personal-sources-card"
import { PresentationTemplatesSection } from "./components/presentation-templates-section"
import { RoutesManagementCard } from "./components/routes-management-card"
import { SegmentAnalysisConfigPanel } from "./components/segment-analysis-config-panel"
import { SkillPackagesManagementCard } from "./components/skill-packages-management-card"
import { SkillsManagementSection } from "./components/skills-management-section"
import { SlideHubManagementSection } from "./components/slide-hub-management-section"
import { TenderModelConfigPanel } from "./components/tender-model-config-panel"
import { ToolsManagementCard } from "./components/tools-management-card"
import { TrainingManagementCard } from "./components/training-management-card"
import { WhatsNewManagementPanel } from "./components/whats-new-management-panel"

/** How a section renders once a customer-admin capability preview is active. */
export type ManagementSectionRenderState = "enabled" | "upsell"

export type ManagementTaskAreaId = "configuration" | "access-audit"

export type ManagementCategoryId =
  | "data-indexing"
  | "ai-models"
  | "agents"
  | "integrations"
  | "communication"
  | "product-features"
  | "access-audit"

export const MANAGEMENT_CATEGORY_IDS: ManagementCategoryId[] = [
  "data-indexing",
  "ai-models",
  "agents",
  "integrations",
  "communication",
  "product-features",
  "access-audit",
]

export type ManagementSectionEntry = {
  /**
   * DOM anchor + scroll-spy id. Must match the id the section component
   * renders itself (via CollapsibleManagementSection), or — when `anchorWrap`
   * is set — the id the registry renderer puts on the wrapping div.
   */
  id: string
  icon: LucideIcon
  titleKey: string
  descriptionFallback?: string
  taskArea: ManagementTaskAreaId
  category?: ManagementCategoryId
  searchTerms?: string[]
  /** English fallback for locales that don't define titleKey yet. */
  titleFallback?: string
  component: ComponentType
  /**
   * The component renders no DOM id of its own, so the registry renderer
   * must provide the anchor element for sidebar scroll/spy to work.
   */
  anchorWrap?: boolean
  /** Only rendered (and listed in the sidebar) when the tender route is enabled. */
  requiresTenderRoute?: boolean
  /**
   * Only rendered (and listed in the sidebar and admin search) when hosted
   * agents are offered on this deployment (`/api/config`
   * `hostedAgentsEnabled`, on-prem only).
   */
  requiresHostedAgents?: boolean
  /**
   * The `core/admin_capabilities.py` key this section is gated behind, for
   * the platform-admin "preview as customer admin" toggle. Sections with no
   * capability mapped here (or whose capability is `platform_only`) are
   * always spliced out of a customer-admin preview — they either predate the
   * capability registry (a real gap, not a design choice — e.g. Slide Hub,
   * Audit Trail) or are intentionally platform-only and have no capability
   * key at all.
   */
  capability?: string
}

export type ResolvedManagementSectionEntry = ManagementSectionEntry & {
  renderState: ManagementSectionRenderState
}

/**
 * Single source of truth for the Management tab: panel render order, sidebar
 * entries, anchor ids, and (in a later step of the admin split) the
 * capability each section is gated behind. Panels and the nav sidebar must
 * never be maintained as separate lists again.
 */
export const MANAGEMENT_SECTIONS: ManagementSectionEntry[] = [
  {
    id: "libraries",
    icon: Database,
    titleKey: "adminDashboard.libraries.title",
    descriptionFallback: "Manage content libraries, search settings and indexing.",
    component: LibrariesManagementCard,
    capability: "libraries",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["library", "libraries", "data", "search", "indexing"],
  },
  {
    id: "indexing",
    icon: RefreshCw,
    titleKey: "adminDashboard.indexing.title",
    descriptionFallback:
      "Run indexing jobs, inspect history and purge a library index when needed.",
    component: IndexingManagementSection,
    capability: "indexing",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["index", "jobs", "purge", "refresh"],
  },
  {
    id: "models",
    icon: Brain,
    titleKey: "adminDashboard.models.title",
    descriptionFallback:
      "Manage model deployments, access and defaults for chat and specialist tasks.",
    component: ModelsManagementCard,
    capability: "models",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["model", "deployment", "reasoning", "effort"],
  },
  {
    id: "image-generation",
    icon: ImagePlus,
    titleKey: "adminDashboard.imageGeneration.title",
    titleFallback: "Image generation",
    descriptionFallback: "Choose the image model Explorer and AI Studio generate with.",
    component: ImageModelConfigPanel,
    capability: "models",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["image", "bild", "flare", "sunburst", "studio", "explorer"],
  },
  {
    id: "tender",
    icon: Zap,
    titleKey: "adminDashboard.tender.title",
    titleFallback: "Tender",
    descriptionFallback: "Choose models for tender extraction, verification, review and chat.",
    component: TenderModelConfigPanel,
    requiresTenderRoute: true,
    capability: "tenders",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["tender", "model", "handoff"],
  },
  {
    id: "segment-analysis",
    icon: BarChart3,
    titleKey: "adminDashboard.segmentAnalysis.title",
    titleFallback: "Segment Analysis",
    descriptionFallback:
      "Set spend limits, refresh rules and approval controls for Segment Analysis.",
    component: SegmentAnalysisConfigPanel,
    capability: "segment_analysis",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["segment", "analysis", "cost", "approval"],
  },
  {
    id: "data-connections",
    icon: Database,
    titleKey: "adminDashboard.dataConnections.title",
    descriptionFallback: "Add and maintain trusted data connections and their access.",
    component: DataConnectionsManagementCard,
    capability: "data_connections",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["database", "sql", "connection", "schema"],
  },
  {
    id: "filth-data-sources",
    icon: Database,
    titleKey: "adminDashboard.filthBackends.title",
    titleFallback: "Prism data sources",
    descriptionFallback: "Manage Prism backends and retrieval mode for this tenant.",
    component: FilthBackendsManagementCard,
    capability: "filth_backends",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["filth", "backend", "source", "search"],
  },
  {
    id: "personal-sources",
    icon: Shield,
    titleKey: "adminDashboard.personalSources.title",
    titleFallback: "Personal sources",
    descriptionFallback:
      "See per-user counts, status and freshness for personal OneDrive, mail and Teams chats.",
    component: PersonalSourcesCard,
    capability: "personal_spaces",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["personal", "onedrive", "mail", "teams", "chat", "consent", "export"],
  },
  {
    id: "routes",
    icon: Route,
    titleKey: "adminDashboard.routes.title",
    descriptionFallback: "Control module routes, visibility and access across the workspace.",
    component: RoutesManagementCard,
    capability: "routes",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["route", "module", "instructions", "settings"],
  },
  {
    id: "training",
    icon: GraduationCap,
    titleKey: "adminDashboard.trainingManagement.title",
    descriptionFallback: "Manage training modules, gating and who can open them.",
    component: TrainingManagementCard,
    capability: "training",
    taskArea: "configuration",
    category: "product-features",
    searchTerms: ["training", "catalog", "gating", "module"],
  },
  {
    id: "features",
    icon: PlugZap,
    titleKey: "adminDashboard.features.title",
    descriptionFallback: "Turn platform features on or off and define who can use them.",
    component: FeaturesManagementCard,
    capability: "features",
    taskArea: "configuration",
    category: "product-features",
    searchTerms: ["feature", "flag", "access", "configuration"],
  },
  {
    id: "tools",
    icon: Wrench,
    titleKey: "adminDashboard.tools.title",
    descriptionFallback: "Manage agent tools, access policies and enabled actions.",
    component: ToolsManagementCard,
    capability: "tools",
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["tool", "action", "agent", "access"],
  },
  {
    id: "mcps",
    icon: Server,
    titleKey: "adminDashboard.mcps.title",
    descriptionFallback: "Manage MCP servers, tools, approval rules and access.",
    component: McpsManagementCard,
    capability: "mcps",
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["mcp", "server", "tool", "agent"],
  },
  {
    id: "plugin-store",
    icon: PlugZap,
    titleKey: "plugins.adminTitle",
    titleFallback: "Plugin store",
    descriptionFallback: "Publish approved plugins and manage company connections.",
    component: PluginsAdmin,
    taskArea: "configuration",
    category: "integrations",
    searchTerms: ["plugin", "store", "api", "oauth", "connection", "distribution"],
  },
  {
    id: "hosted-agents",
    icon: Bot,
    titleKey: "adminDashboard.hostedAgents.title",
    titleFallback: "Hosted agents",
    descriptionFallback: "Manage hosted role agents: templates, access, pause and secrets.",
    component: HostedAgentsManagementCard,
    capability: "hosted_agents",
    requiresHostedAgents: true,
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["hosted", "agent", "hermes", "role agent"],
  },
  {
    id: "llm-pool",
    icon: Server,
    titleKey: "adminDashboard.llmPool.title",
    descriptionFallback: "Configure model endpoints, failover and cooldown behavior.",
    component: LLMPoolConfigPanel,
    capability: "llm_pool",
    taskArea: "configuration",
    category: "ai-models",
    searchTerms: ["llm", "pool", "endpoint", "failover"],
  },
  {
    id: "docint",
    icon: FileText,
    titleKey: "adminDashboard.docint.configTitle",
    descriptionFallback: "Set Document Intelligence quotas and user overrides.",
    component: DocintConfigPanel,
    capability: "docint",
    taskArea: "configuration",
    category: "data-indexing",
    searchTerms: ["document intelligence", "quota", "ocr"],
  },
  {
    id: "alerts",
    icon: Bell,
    titleKey: "adminDashboard.alerts.title",
    descriptionFallback: "Configure alert channels, routing, cooldowns and delivery history.",
    component: AlertsConfigPanel,
    capability: "alerts",
    taskArea: "configuration",
    category: "communication",
    searchTerms: ["alert", "email", "webhook", "routing", "cooldown"],
  },
  {
    id: "middleware",
    icon: Layers,
    titleKey: "adminDashboard.systemConfig.middlewareSectionTitle",
    titleFallback: "Agent middleware",
    descriptionFallback: "Set the middleware chain applied to each agent invocation.",
    component: MiddlewareManagementSection,
    capability: "middleware",
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["middleware", "agent", "chain"],
  },
  {
    id: "skill-packages",
    icon: Layers,
    titleKey: "adminDashboard.skillPackages.title",
    titleFallback: "Skill packages",
    descriptionFallback: "Import, publish and manage installable skill packages for hosted agents.",
    component: SkillPackagesManagementCard,
    capability: "hosted_agents",
    requiresHostedAgents: true,
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["skill", "package", "hosted", "agent", "install"],
  },
  {
    id: "skills",
    icon: FileText,
    titleKey: "adminDashboard.systemConfig.skillGuidanceSectionTitle",
    titleFallback: "Skill guidance",
    descriptionFallback: "Manage global skill guidance and autoload rules.",
    component: SkillsManagementSection,
    capability: "skills",
    taskArea: "configuration",
    category: "agents",
    searchTerms: ["skill", "guidance", "agent"],
  },
  {
    id: "presentation-templates",
    icon: Presentation,
    titleKey: "adminDashboard.systemConfig.presentationTemplate.sectionTitle",
    titleFallback: "Presentation templates",
    descriptionFallback: "Choose how presentations are generated and manage brand specifications.",
    component: PresentationTemplatesSection,
    capability: "presentation_templates",
    taskArea: "configuration",
    category: "integrations",
    searchTerms: ["presentation", "template", "layout", "brand spec", "pptx", "logo", "markdown"],
  },
  {
    id: "slide-hub",
    icon: MonitorPlay,
    titleKey: "adminDashboard.systemConfig.slideHub.sectionTitle",
    titleFallback: "Slide Hub presenting",
    descriptionFallback: "Choose the presenting modes available in Slide Hub.",
    component: SlideHubManagementSection,
    taskArea: "configuration",
    category: "integrations",
    searchTerms: ["slide", "hub", "presenting"],
    // No capability defined in core/admin_capabilities.py yet — predates the
    // registry (added by #2337 before the admin split existed). Fully hidden
    // in customer-admin preview until a capability is formalized for it.
  },
  {
    id: "admin-groups",
    icon: Shield,
    titleKey: "adminDashboard.adminGroups.title",
    descriptionFallback:
      "Manage which users and groups have administrative access to different features.",
    component: AdminGroupsPanel,
    anchorWrap: true,
    capability: "admin_groups",
    taskArea: "access-audit",
    searchTerms: ["admin", "group", "role", "access"],
  },
  {
    id: "flow-creation-policy",
    icon: Workflow,
    titleKey: "adminDashboard.flowPolicy.title",
    titleFallback: "Flow creation",
    descriptionFallback: "Control who can create and share flows.",
    component: FlowCreationPolicyPanel,
    anchorWrap: true,
    capability: "flows",
    taskArea: "configuration",
    category: "product-features",
    searchTerms: ["flow", "creation", "policy"],
  },
  {
    id: "branding",
    icon: Palette,
    titleKey: "adminDashboard.branding.title",
    titleFallback: "Branding",
    descriptionFallback: "Tenant names, colors, logos, PWA manifest and chat footer links.",
    component: BrandingConfigPanel,
    capability: "branding",
    taskArea: "configuration",
    category: "communication",
    searchTerms: ["logo", "favicon", "theme", "PWA", "white-label", "branding"],
  },
  {
    id: "flow-email-branding",
    icon: Mail,
    titleKey: "adminDashboard.flowEmailBranding.title",
    titleFallback: "Flow email branding",
    descriptionFallback: "Configure the sender, recipients and branding used by Flow emails.",
    component: FlowEmailBrandingPanel,
    anchorWrap: true,
    capability: "flows",
    taskArea: "configuration",
    category: "communication",
    searchTerms: ["flow", "email", "branding", "smtp"],
  },
  {
    id: "audit-trail",
    icon: FileText,
    titleKey: "adminDashboard.auditTrail.title",
    descriptionFallback: "Review administrative changes, actors and affected resources.",
    component: AuditTrailSection,
    taskArea: "access-audit",
    searchTerms: ["audit", "history", "changes"],
    // No capability defined — intentionally platform-only per the epic's
    // own out-of-scope note (customer visibility needs allowlist redaction
    // first). Fully hidden in customer-admin preview.
  },
  {
    id: "whats-new",
    icon: Sparkles,
    titleKey: "adminDashboard.whatsNew.title",
    titleFallback: "What's New",
    descriptionFallback: "Review, pin and refresh customer-facing release updates.",
    component: WhatsNewManagementPanel,
    anchorWrap: true,
    capability: "whats_new",
    taskArea: "configuration",
    category: "communication",
    searchTerms: ["what's new", "release", "version", "pin"],
  },
]

/**
 * @param capabilityModes When provided (customer-admin preview is active),
 * filters to only sections whose `capability` resolves to `available` or
 * `manage` — everything else (no capability mapped, capability absent from
 * the map because it's `platform_only`, or resolved to `hidden`) is spliced
 * out entirely, matching what a real customer admin would never see at all.
 * Without it, every tender-gated section renders normally (the platform
 * admin's own real view).
 * @param hostedAgentsEnabled Hosted agents are offered on this deployment;
 * when false (the default — fail closed) the Hosted agents and Skill packages
 * sections are left out entirely.
 */
export function getManagementSections(
  hasTenderRoute: boolean,
  capabilityModes?: Record<string, CapabilityMode>,
  hostedAgentsEnabled = false
): ResolvedManagementSectionEntry[] {
  const base = MANAGEMENT_SECTIONS.filter(
    (section) =>
      (hasTenderRoute || !section.requiresTenderRoute) &&
      (hostedAgentsEnabled || !section.requiresHostedAgents)
  )

  if (!capabilityModes) {
    return base.map((section) => ({ ...section, renderState: "enabled" }))
  }

  const resolved: ResolvedManagementSectionEntry[] = []
  for (const section of base) {
    const mode = section.capability ? capabilityModes[section.capability] : undefined
    if (mode === "manage") {
      resolved.push({ ...section, renderState: "enabled" })
    } else if (mode === "available") {
      resolved.push({ ...section, renderState: "upsell" })
    }
    // mode === "hidden" or undefined (no capability, or a platform_only
    // capability absent from a delegable-only map): spliced out entirely.
  }
  return resolved
}

export function getManagementSectionsForArea(
  sections: ResolvedManagementSectionEntry[],
  taskArea: ManagementTaskAreaId
): ResolvedManagementSectionEntry[] {
  return sections.filter((section) => section.taskArea === taskArea)
}

export function searchManagementSections(
  sections: ResolvedManagementSectionEntry[],
  query: string,
  getTitle: (section: ResolvedManagementSectionEntry) => string
): ResolvedManagementSectionEntry[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return sections
  return sections.filter((section) => {
    const haystack = [
      getTitle(section),
      section.id,
      section.category ?? "",
      ...(section.searchTerms ?? []),
    ]
      .join(" ")
      .toLocaleLowerCase()

    return haystack.includes(normalized)
  })
}
